import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { FiMoreVertical } from 'react-icons/fi';
import { Button } from './Button'; // Adjust path if necessary

export const ActionMenu = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isMounted, setIsMounted] = useState(false);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const toggleMenu = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 224; // w-56
      
      // Check if there's enough space on the right, otherwise open to the left
      const leftPosition = window.innerWidth - rect.right < menuWidth 
        ? rect.left - menuWidth + rect.width
        : rect.right - menuWidth;

      setPosition({
        top: rect.bottom + 5,
        left: leftPosition,
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const DropdownMenu = (
    <div
      ref={menuRef}
      // UPDATED: Replaced hardcoded colors with theme-aware classes
      className="fixed w-56 rounded-md bg-card-color-100 dark:bg-gray-800 shadow-lg border border-slate-200 dark:border-gray-700 focus:outline-none z-[10]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="py-1" role="menu" aria-orientation="vertical">
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type === ActionMenuItem) {
            return React.cloneElement(child, { onClose: closeMenu });
          }
          return child;
        })}
      </div>
    </div>
  );

  return (
    <>
      <Button ref={buttonRef} variant="ghost" size="sm" onClick={toggleMenu}>
        {/* UPDATED: Replaced hardcoded color with a theme-aware class */}
        <FiMoreVertical className="h-5 w-5 text-title-page-color-300 dark:text-gray-400" />
      </Button>
      {isOpen ? createPortal(DropdownMenu, document.body) : null}
    </>
  );
};


// Helper component for the items inside the menu
// Note: Added `as` prop to support rendering as a Link or other component
export const ActionMenuItem = ({ as: Component = 'button', icon: Icon, label, onClick, className, disabled, onClose, ...props }) => {
  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <Component
      onClick={handleClick}
      disabled={disabled}
      // UPDATED: Replaced hardcoded colors with theme-aware classes
      className={cn(
        'w-full text-left flex items-center px-4 py-2 text-sm text-title-page-color-100 dark:text-gray-300 hover:bg-card-color-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      {...props} // Pass remaining props like `to` for React Router's Link
    >
      {Icon && <Icon className="mr-3 h-4 w-4" aria-hidden="true" />}
      <span>{label}</span>
    </Component>
  );
};